import React, { useState, useEffect } from 'react';
import designApi from '@services/designApi.jsx';
import ResourceUploadField from '@components/common/ResourceUploadField.jsx';

const QrPageSetting = ({ themeId, themePageMaster }) => {
  // 상태 관리
  const [qrViewerPage, setQrViewerPage] = useState({
    themePageMaster: themePageMaster,
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // API 호출로 데이터 가져오기
  useEffect(() => {
    const fetchQrViewerPage = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const data = await designApi.getQrViewerPage(themeId);
        
        setQrViewerPage({
          ...data
        });

        console.log("API 호출 성공:", data);
      } catch (err) {
        console.error("API 호출 실패:", err);
        setError(err);
        // 에러 발생 시 기본 데이터 유지
      } finally {
        setIsLoading(false);
      }
    };

    fetchQrViewerPage();
  }, [themeId]);

  // 입력값 변경 핸들러
  const handleInputChange = (field, value) => {
    setQrViewerPage(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // 리소스 업로드 처리 함수
  const handleResourceUploaded = (resourceType, resourceId) => {
    setQrViewerPage({
      ...qrViewerPage,
      [resourceType]: resourceId
    });
  };
  
  // 저장 이벤트 리스너 (페이지 상위 컴포넌트에서 저장 요청 시 실행)
  useEffect(() => {
    const eventName = `savePageDesign_qr_page`; // 지정된 이벤트 이름 사용

    const saveHandler = async (event) => {
      try {
        // 저장 중임을 표시
        setIsLoading(true);

        // 자체 로직을 사용하여 데이터 저장
        const dataToSave = {
          ...qrViewerPage,
          themeId: event.detail.themeId // themeId를 명시적으로 포함
        };
        const isSucceed = await designApi.saveQrViewerPage(event.detail.themeId, dataToSave);

        if(isSucceed) {
          // 성공 메시지
          alert("저장되었습니다.");
        } else {
          // 실패 메시지
          alert("저장에 실패했습니다.");
        }

      } catch (error) {
        // 오류 처리
        console.error("저장 실패:", error);
        alert("저장 중 오류가 발생했습니다.");
      } finally {
        setIsLoading(false);
      }
    };

    // 이벤트 리스너 등록
    document.addEventListener(eventName, saveHandler);

    // 컴포넌트 언마운트 시 리스너 제거
    return () => {
      document.removeEventListener(eventName, saveHandler);
    };
  }, [qrViewerPage, themeId]); // 의존성 배열에 필요한 상태 포함

  return (
      <div className="design-settings">
          {/* 언어 선택 섹션 */}
          <div className="settings-box">
              <h4 className="settings-title">언어 선택</h4>
              <div className="option-select-container">
                  <select
                      className="option-select"
                      value={qrViewerPage.languageCode || "ko"}
                      onChange={(e) => handleInputChange('languageCode', e.target.value)}
                  >
                      <option value="ko">한국어</option>
                      <option value="en">영어</option>
                      <option value="jp">일본어</option>
                      <option value="cn">중국어</option>
                  </select>
                  <i className="ri-arrow-down-s-line select-arrow"></i>
              </div>
          </div>

          {/* 배경 이미지 섹션 */}
          <div className="settings-box">
              <h4 className="settings-title">배경 이미지</h4>
              <ResourceUploadField
                  label=""
                  resourceId={qrViewerPage.backgroundResourceId}
                  onResourceUploaded={(id) =>
                      handleResourceUploaded("backgroundResourceId", id)
                  }
                  acceptTypes="image/*"
              />
          </div>

          {/* 배경 음악 섹션 */}
          <div className="settings-box">
              <h4 className="settings-title">배경 음악</h4>
              <ResourceUploadField
                  label=""
                  resourceId={qrViewerPage.backgroundMusicResourceId}
                  onResourceUploaded={(id) =>
                      handleResourceUploaded("backgroundMusicResourceId", id)
                  }
                  acceptTypes="audio/*"
              />
          </div>

          {/* 로고 섹션 */}
          <div className="settings-box">
              <h4 className="settings-title">로고</h4>
              <ResourceUploadField
                  label=""
                  resourceId={qrViewerPage.logoResourceId}
                  onResourceUploaded={(id) =>
                      handleResourceUploaded("logoResourceId", id)
                  }
                  acceptTypes="image/*"
              />
          </div>

          {/* 문구 섹션 */}
          <div className="settings-box">
              <h4 className="settings-title">안내 문구</h4>
              <div className="settings-row">
                  <div className="settings-input">
                      <input
                          type="text"
                          placeholder="안내 문구를 입력해주세요"
                          className="text-input"
                          value={qrViewerPage.guideText || ""}
                          onChange={(e) => handleInputChange('guideText', e.target.value)}
                      />
                  </div>
              </div>
              <div className="settings-row">
                  <div className="settings-label">색상</div>
                  <div className="color-picker-container">
                      <input
                          type="text"
                          className="color-input"
                          value={qrViewerPage.guideTextColor || "#000000"}
                          readOnly
                      />
                      <input
                          type="color"
                          className="color-picker"
                          value={qrViewerPage.guideTextColor || "#000000"}
                          onChange={(e) => handleInputChange('guideTextColor', e.target.value)}
                      />
                  </div>
              </div>
          </div>

          {/* 문구 섹션 */}
          <div className="settings-box">
              <h4 className="settings-title">오류 문구</h4>
              <div className="settings-row">
                  <div className="settings-input">
                      <input
                          type="text"
                          placeholder="오류 문구를 입력해주세요"
                          className="text-input"
                          value={qrViewerPage.errorText || ""}
                          onChange={(e) => handleInputChange('errorText', e.target.value)}
                      />
                  </div>
              </div>
              <div className="settings-row">
                  <div className="settings-label">색상</div>
                  <div className="color-picker-container">
                      <input
                          type="text"
                          className="color-input"
                          value={qrViewerPage.errorTextColor || "#000000"}
                          readOnly
                      />
                      <input
                          type="color"
                          className="color-picker"
                          value={qrViewerPage.errorTextColor || "#000000"}
                          onChange={(e) => handleInputChange('errorTextColor', e.target.value)}
                      />
                  </div>
              </div>
          </div>

          {/* 버튼 섹션 */}
          <div className="settings-box">
              <h4 className="settings-title">다운로드 버튼</h4>
              <ResourceUploadField
                  label="사진"
                  resourceId={qrViewerPage.photoDownloadButtonResourceId}
                  onResourceUploaded={(id) =>
                      handleResourceUploaded("photoDownloadButtonResourceId", id)
                  }
                  acceptTypes="image/*"
              />
              <ResourceUploadField
                  label="동영상"
                  resourceId={qrViewerPage.videoDownloadButtonResourceId}
                  onResourceUploaded={(id) =>
                      handleResourceUploaded("videoDownloadButtonResourceId", id)
                  }
                  acceptTypes="image/*"
              />
          </div>
      </div>
  );
};

export default QrPageSetting;